import {
  formatCurrency,
  getSinglarOrPluralWord,
  isCardLikeAssignmentPaymentMethod,
  formatNumber,
} from '../../../../utilities/helpers'

import Card from 'react-bootstrap/Card'
import Stack from 'react-bootstrap/Stack'

import styles from './InfoCard.module.scss'

function ClockIcon({ className }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.25" />
      <path
        d="M12 7v5l3 2"
        stroke="currentColor"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export default function InfoCard({
  paymentPlans,
  installmentMonths,
  total,
  pricingObject,
  price,
  paymentMethod,
  children,
  customFeeTotal = 0,
  customFeeLineItems = [],
  showZeroServiceFee = false,
  isPackage,
  requiresDonation = false,
  donationAmount = 0,
  donationLabel = 'Donation',
  donationLineItems = [],
}) {
  const effectivePricePerTicket =
    Number(pricingObject?.pricePerTicket ?? price ?? 0) || 0
  const hasPricePerTicketSet =
    Number.isFinite(Number(pricingObject?.pricePerTicket)) ||
    (price !== undefined &&
      price !== null &&
      String(price).trim() !== '' &&
      Number.isFinite(Number(price)))

  const showServiceFee =
    showZeroServiceFee || Number(pricingObject?.serviceFee) !== 0

  return (
    <Card className={`${styles.wrap} card-with-border`}>
      <Card.Body className={styles.cardBody}>
        {children != null && children !== false && (
          <div className={styles.header}>
            <ClockIcon className={styles.headerIcon} />
            <div className={styles.headerText}>{children}</div>
          </div>
        )}
        {paymentPlans && Object.keys(paymentPlans)?.length > 0 && (
          <ul className={`mt-0 mb-0 ${styles.paymentPlans}`}>
            <li className="list list-sm list-item list-item--vertical list-item-lg list-item-lg--sm">
              <Stack as="ul" gap={1}>
                <Stack as="li" direction="horizontal" className="split-row">
                  <span>Pay today:</span>
                  <span>{formatCurrency(paymentPlans[1]?.amount)}</span>
                </Stack>
                <Stack as="li" direction="horizontal" className="split-row">
                  <span>Then:</span>
                  <span>
                    {formatNumber(installmentMonths - 1)} monthly{' '}
                    {getSinglarOrPluralWord(installmentMonths - 1, 'payment')}{' '}
                    of {formatCurrency(paymentPlans[2]?.amount)}
                  </span>
                </Stack>
              </Stack>
            </li>
          </ul>
        )}
        <div className={styles.summary}>
          <div className={styles.summaryTitle}>Order summary</div>
          <div className={styles.rowGroup}>
            <div className={styles.row}>
              <span>
                Ticket price ({formatCurrency(effectivePricePerTicket)} x{' '}
                {formatNumber(pricingObject?.ticketCount)})
              </span>
              <span>
                {formatCurrency(
                  effectivePricePerTicket * pricingObject?.ticketCount
                )}
              </span>
            </div>
            {isPackage && requiresDonation && donationLineItems.length > 0
              ? donationLineItems.map((line) => (
                  <div key={line.label} className={styles.row}>
                    <span>{line.label}:</span>
                    <span>{formatCurrency(line.amount)}</span>
                  </div>
                ))
              : isPackage &&
                requiresDonation && (
                  <div className={styles.row}>
                    <span>{donationLabel}:</span>
                    <span>{formatCurrency(donationAmount)}</span>
                  </div>
                )}
            {showServiceFee && (
              <div className={styles.row}>
                <span>
                  {pricingObject?.isFlatServiceFee
                    ? 'Service Fee (package)'
                    : 'Service Fee'}
                </span>
                <span>
                  {hasPricePerTicketSet
                    ? formatCurrency(pricingObject?.serviceFee)
                    : '--'}
                </span>
              </div>
            )}
            {Number(pricingObject?.ticketFacilityFee) > 0 && (
              <div className={styles.row}>
                <span>Facility Fee</span>
                <span>
                  {hasPricePerTicketSet
                    ? formatCurrency(pricingObject?.ticketFacilityFee)
                    : '--'}
                </span>
              </div>
            )}
          </div>
          <hr className={styles.dividerDashed} />
          <div className={styles.rowGroup}>
            {customFeeLineItems.length > 0
              ? customFeeLineItems.map((line) => (
                  <div key={line.rowId} className={styles.row}>
                    <span>{line.name}</span>
                    <span>{formatCurrency(line.amount)}</span>
                  </div>
                ))
              : Number(customFeeTotal) > 0 && (
                  <div className={styles.row}>
                    <span>Custom fees</span>
                    <span>{formatCurrency(customFeeTotal)}</span>
                  </div>
                )}
            <div className={styles.row}>
              <span>Sales Tax</span>
              <span>{formatCurrency(pricingObject?.salesTax)}</span>
            </div>
            {isCardLikeAssignmentPaymentMethod(paymentMethod) && (
              <div className={styles.row}>
                <span>Processing Fee</span>
                <span>
                  {formatCurrency(pricingObject?.paymentProcessingFee)}
                </span>
              </div>
            )}
          </div>
          <hr className={styles.dividerSolid} />
          <div className={styles.footerRow}>
            <span>Total</span>
            <span>{formatCurrency(total)}</span>
          </div>
        </div>
      </Card.Body>
    </Card>
  )
}
